import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, Github, Code2, Palette, Layers } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface PremiumProjectCardProps {
    id: string;
    title: string;
    summary: string;
    coverImage: string;
    slug: string;
    year: number;
    client?: string;
    disciplines?: string[];
    techStack?: string[];
    projectType: 'design' | 'dev' | 'integrated';
    demoUrl?: string | null;
    githubUrl?: string | null;
    index: number;
}

export const PremiumProjectCard: React.FC<PremiumProjectCardProps> = ({
    title,
    summary,
    coverImage,
    slug,
    year,
    client,
    disciplines = [],
    techStack = [],
    projectType,
    demoUrl,
    githubUrl,
    index
}) => {
    const isDevProject = projectType === 'dev';
    const isIntegrated = projectType === 'integrated';

    // Determine the icon/label based on type
    let TypeIcon = Palette;
    let typeLabel = 'Design';
    let typeGradient = "from-purple-500 to-pink-500";

    if (isDevProject) {
        TypeIcon = Code2;
        typeLabel = 'Development';
        typeGradient = "from-blue-500 to-cyan-500";
    } else if (isIntegrated) {
        TypeIcon = Layers;
        typeLabel = 'Integrated';
        typeGradient = "from-amber-500 to-orange-500";
    }

    return (
        <motion.div
            className="group relative w-full h-full flex flex-col bg-zinc-950/80 backdrop-blur-xl rounded-[2rem] border border-white/10 overflow-hidden"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1, ease: [0.21, 0.47, 0.32, 0.98] }}
            whileHover={{ y: -8 }}
        >
            {/* Ambient Background Glow on Hover */}
            <div className={`absolute inset-0 bg-gradient-to-br ${typeGradient} opacity-0 group-hover:opacity-5 transition-opacity duration-700 pointer-events-none`} />

            {/* Cover Image Container */}
            <div className="relative aspect-video w-full overflow-hidden">
                {/* Image */}
                <img
                    src={coverImage}
                    alt={title}
                    className="w-full h-full object-cover transition-transform duration-700 ease-in-out group-hover:scale-105"
                />

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent opacity-80" />

                {/* Top Badge */}
                <div className="absolute top-4 left-4 z-20">
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-950/60 backdrop-blur-md border border-white/10 shadow-lg">
                        <TypeIcon className="w-3.5 h-3.5 text-white" />
                        <span className="text-xs font-semibold text-white tracking-wide uppercase">{typeLabel}</span>
                    </div>
                </div>

                {/* Corner Action */}
                <div className="absolute top-4 right-4 z-20 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                    <div className="p-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white">
                        <ExternalLink className="w-4 h-4" />
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex flex-col flex-grow p-8 pt-6 relative z-10">
                {/* Meta Header */}
                <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-zinc-400 tracking-tight">{client || 'Personal Project'}</span>
                    <span className="text-xs font-mono text-zinc-500 px-2 py-1 rounded-md bg-white/5 border border-white/5">
                        {year}
                    </span>
                </div>

                {/* Title */}
                <Link to={`/projetos/${slug}`} className="block group/title">
                    <h3 className="text-2xl font-bold text-white mb-3 group-hover/title:text-blue-400 transition-colors leading-tight">
                        {title}
                    </h3>
                </Link>

                {/* Summary */}
                <p className="text-zinc-400 text-sm leading-relaxed line-clamp-2 mb-6 font-light">
                    {summary}
                </p>

                {/* Tags / Tech Stack */}
                <div className="mt-auto">
                    <div className="flex flex-wrap gap-2 mb-6">
                        {(isDevProject ? techStack : disciplines).slice(0, 3).map((item, i) => (
                            <span
                                key={i}
                                className="px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-zinc-300 bg-white/5 border border-white/5 rounded-md"
                            >
                                {item}
                            </span>
                        ))}
                    </div>

                    {/* Actions */}
                    <div className="pt-5 border-t border-white/5 flex items-center justify-between">
                        {isDevProject ? (
                            <div className="flex gap-3 w-full">
                                {demoUrl && (
                                    <Button asChild size="sm" className="flex-1 bg-white/5 hover:bg-white/10 text-white border-0 h-9 rounded-xl">
                                        <a href={demoUrl} target="_blank" rel="noopener noreferrer">
                                            <ExternalLink className="w-3.5 h-3.5 mr-2" />
                                            Demo
                                        </a>
                                    </Button>
                                )}
                                {githubUrl && (
                                    <Button asChild size="sm" className="flex-1 bg-transparent hover:bg-white/5 text-zinc-400 hover:text-white border border-white/10 h-9 rounded-xl">
                                        <a href={githubUrl} target="_blank" rel="noopener noreferrer">
                                            <Github className="w-3.5 h-3.5 mr-2" />
                                            Code
                                        </a>
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <Link
                                to={`/projetos/${slug}`}
                                className="w-full group/btn inline-flex items-center justify-between text-sm font-medium text-white hover:text-blue-400 transition-colors"
                            >
                                <span>Ver Detalhes do Projeto</span>
                                <ArrowRight className="w-4 h-4 transform group-hover/btn:translate-x-1 transition-transform" />
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </motion.div>
    );
};
